package com.kaihuoy.components.common

import androidx.compose.runtime.*
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import com.kaihuoy.components.common.*

data class DropdownOption(val value: String, val label: String)

fun List<String>.toDropdownOptions(): List<DropdownOption> = map { DropdownOption(it, it) }

@Composable
fun StyledDropdown(
    value: String,
    options: List<DropdownOption>,
    placeholder: String = "Valitse...",
    label: String = "",
    onSelect: (String) -> Unit,
    fullWidth: Boolean = true,
    minWidth: CSSLengthOrPercentageValue? = null
) {
    var isOpen by remember { mutableStateOf(false) }
    var hoveredIndex by remember { mutableStateOf(-1) }
    var containerEl by remember { mutableStateOf<HTMLElement?>(null) }

    val selectedLabel = options.find { it.value == value }?.label ?: ""
    val displayText = selectedLabel.ifEmpty { placeholder }
    val hasValue = value.isNotEmpty()

    DisposableEffect(isOpen) {
        if (!isOpen) return@DisposableEffect onDispose {}
        val handler: (Event) -> Unit = { e ->
            val target = e.target as? HTMLElement
            if (containerEl?.contains(target) == false) {
                isOpen = false
            }
        }
        document.addEventListener("mousedown", handler)
        onDispose { document.removeEventListener("mousedown", handler) }
    }

    if (label.isNotEmpty()) {
        Label(attrs = {
            style {
                display(DisplayStyle.Block)
                fontWeight("500")
                marginBottom(6.px)
                color(if (isOpen) Color("#00d4ff") else KaihuTheme.TextPrimary)
                fontSize(0.875.em)
                property("transition", "color 0.2s ease")
            }
        }) { Text(label) }
    }

    Div(attrs = {
        ref { el ->
            containerEl = el
            onDispose { containerEl = null }
        }
        style {
            property("position", "relative")
            if (fullWidth) width(100.percent)
            minWidth?.let { property("min-width", it.toString()) }
        }
    }) {
        // ── Trigger ──
        Div(attrs = {
            onClick { isOpen = !isOpen; hoveredIndex = -1 }
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                gap(8.px)
                padding(10.px, 14.px)
                backgroundColor(Color("#1a2535"))
                borderRadius(8.px)
                property(
                    "border",
                    if (isOpen) "1px solid #00d4ff" else "1px solid rgba(255,255,255,0.08)"
                )
                property(
                    "box-shadow",
                    if (isOpen) "0 0 0 2px rgba(0,212,255,0.15)" else "none"
                )
                color(if (hasValue) Color("#ffffff") else Color("#8892a4"))
                cursor("pointer")
                property("user-select", "none")
                fontSize(1.em)
                boxSizing("border-box")
                property("transition", "border-color 0.2s, box-shadow 0.2s")
                if (fullWidth) width(100.percent)
            }
        }) {
            Span(attrs = {
                style {
                    property("white-space", "nowrap")
                    overflow("hidden")
                    property("text-overflow", "ellipsis")
                    flex(1)
                }
            }) { Text(displayText) }

            // Chevron — rotates when open
            Span(attrs = {
                style {
                    property("flex-shrink", "0")
                    property("line-height", "1")
                    property("transition", "transform 0.2s ease")
                    if (isOpen) property("transform", "rotate(180deg)")
                    color(Color("#8892a4"))
                    fontSize(0.7.em)
                }
            }) {
                Text("▼")
            }
        }

        // ── Listbox ──
        if (isOpen) {
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("top", "calc(100% + 4px)")
                    property("left", "0")
                    property("right", "0")
                    backgroundColor(Color("#1a2535"))
                    property("border", "1px solid #00d4ff")
                    borderRadius(8.px)
                    property("box-shadow", "0 8px 32px rgba(0,0,0,0.55)")
                    property("z-index", "9999")
                    property("max-height", "240px")
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                }
            }) {
                options.forEachIndexed { idx, option ->
                    val isSelected = option.value == value
                    val isHov = hoveredIndex == idx
                    Div(attrs = {
                        onClick {
                            onSelect(option.value)
                            isOpen = false
                        }
                        onMouseEnter { hoveredIndex = idx }
                        onMouseLeave { hoveredIndex = -1 }
                        style {
                            padding(10.px, 14.px)
                            if (isSelected) paddingLeft(12.px)
                            cursor("pointer")
                            fontSize(1.em)
                            color(if (isSelected) Color("#00d4ff") else Color("#ffffff"))
                            property(
                                "background-color",
                                when {
                                    isHov    -> "rgba(0,212,255,0.12)"
                                    isSelected -> "rgba(0,212,255,0.08)"
                                    else     -> "transparent"
                                }
                            )
                            if (isSelected) property("border-left", "2px solid #00d4ff")
                            property("transition", "background-color 0.12s")
                        }
                    }) {
                        Text(option.label)
                    }
                }
            }
        }
    }
}

// Thin wrapper so callers don't need to wrap in a Div for the label+margin pattern
@Composable
fun StyledDropdownField(
    label: String,
    value: String,
    options: List<DropdownOption>,
    placeholder: String = "Valitse...",
    onSelect: (String) -> Unit,
    fullWidth: Boolean = true,
    minWidth: CSSLengthOrPercentageValue? = null
) {
    Div(attrs = { style { marginBottom(16.px) } }) {
        StyledDropdown(
            value = value,
            options = options,
            placeholder = placeholder,
            label = label,
            onSelect = onSelect,
            fullWidth = fullWidth,
            minWidth = minWidth
        )
    }
}
